import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Link2,
  Unlink,
  RefreshCcw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ExternalLink,
  Building2,
  Wallet,
  ArrowRightLeft,
  Clock,
  Settings,
  ChevronDown,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { cn } from '../../lib/utils';

// ============== Types ==============
interface SchwabStatus {
  configured: boolean;
  connected: boolean;
  custodian: string;
  lastSyncAt?: string;
  lastSyncStatus?: string;
  accountCount?: number;
  environment?: string;
}

interface SchwabAccount {
  accountNumber: string;
  hashValue: string;
  type: string;
  name?: string;
  linked: boolean;
  portfolioId?: number;
  portfolioName?: string;
  lastPositionSync?: string;
}

interface SyncResult {
  success: boolean;
  created: number;
  updated: number;
  errors: string[];
  accounts?: SchwabAccount[];
}

// ============== Styled Components ==============
const Container = styled.div`
  padding: 1.5rem;
`;

const Card = styled(motion.div)`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  h3 {
    font-size: 1rem;
    font-weight: 600;
    color: white;
    margin: 0;
  }
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

const StatusBadge = styled.span<{ status: 'connected' | 'disconnected' | 'warning' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  ${props => props.status === 'connected' && `
    background: rgba(34, 197, 94, 0.15);
    color: #22c55e;
    border: 1px solid rgba(34, 197, 94, 0.3);
  `}

  ${props => props.status === 'disconnected' && `
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.3);
  `}

  ${props => props.status === 'warning' && `
    background: rgba(245, 158, 11, 0.15);
    color: #f59e0b;
    border: 1px solid rgba(245, 158, 11, 0.3);
  `}
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' && `
    background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
    color: white;
    border: none;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
    }
  `}

  ${props => props.variant === 'secondary' && `
    background: rgba(255, 255, 255, 0.05);
    color: #94a3b8;
    border: 1px solid rgba(255, 255, 255, 0.1);

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }
  `}

  ${props => props.variant === 'danger' && `
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.3);

    &:hover:not(:disabled) {
      background: rgba(239, 68, 68, 0.25);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const InfoItem = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  padding: 1rem;

  .label {
    font-size: 0.75rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 4px;
  }

  .value {
    font-size: 0.875rem;
    color: white;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

const AccountList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const AccountItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.05);

  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const AccountInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  .icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: rgba(59, 130, 246, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #3b82f6;
  }

  .details {
    .name {
      font-size: 0.875rem;
      font-weight: 600;
      color: white;
    }

    .meta {
      font-size: 0.75rem;
      color: #64748b;
    }
  }
`;

const Alert = styled.div<{ type: 'info' | 'success' | 'warning' | 'error' }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 1rem;
  border-radius: 10px;
  margin-bottom: 1rem;

  ${props => props.type === 'info' && `
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.2);
    color: #60a5fa;
  `}

  ${props => props.type === 'success' && `
    background: rgba(34, 197, 94, 0.1);
    border: 1px solid rgba(34, 197, 94, 0.2);
    color: #4ade80;
  `}

  ${props => props.type === 'warning' && `
    background: rgba(245, 158, 11, 0.1);
    border: 1px solid rgba(245, 158, 11, 0.2);
    color: #fbbf24;
  `}

  ${props => props.type === 'error' && `
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.2);
    color: #f87171;
  `}

  .content {
    flex: 1;
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

// ============== Component ==============
const SchwabIntegration: React.FC = () => {
  const [status, setStatus] = useState<SchwabStatus | null>(null);
  const [accounts, setAccounts] = useState<SchwabAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [expandedAccount, setExpandedAccount] = useState<string | null>(null);

  // Fetch Schwab connection status
  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get('/api/custodian/schwab/status');
      setStatus(response.data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch Schwab status:', err);
      setStatus({ configured: false, connected: false, custodian: 'SCHWAB' });
    }
  }, []);

  // Fetch linked accounts
  const fetchAccounts = useCallback(async () => {
    try {
      const response = await axios.get('/api/custodian/schwab/accounts');
      setAccounts(response.data);
    } catch (err: any) {
      console.error('Failed to fetch accounts:', err);
      setAccounts([]);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchStatus();
      await fetchAccounts();
      setLoading(false);
    };
    init();
  }, [fetchStatus, fetchAccounts]);

  // Handle OAuth redirect callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && state) {
      // Handle OAuth callback
      handleOAuthCallback(code, state);
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleOAuthCallback = async (code: string, state: string) => {
    try {
      setConnecting(true);
      await axios.post('/api/custodian/schwab/oauth/callback', { code, state });
      await fetchStatus();
      await fetchAccounts();
      setError(null);
    } catch (err: any) {
      setError('Failed to complete OAuth connection: ' + (err.response?.data?.message || err.message));
    } finally {
      setConnecting(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      const redirectUri = `${window.location.origin}/settings/integrations`;
      const response = await axios.post('/api/custodian/schwab/oauth/initiate', { redirectUri });

      // Redirect to Schwab authorization page
      if (response.data.authorizationUrl) {
        window.location.href = response.data.authorizationUrl;
      }
    } catch (err: any) {
      setError('Failed to initiate OAuth connection: ' + (err.response?.data?.message || err.message));
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect from Schwab? This will revoke access tokens.')) {
      return;
    }

    try {
      await axios.post('/api/custodian/schwab/disconnect');
      await fetchStatus();
      setAccounts([]);
      setSyncResult(null);
    } catch (err: any) {
      setError('Failed to disconnect: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSyncAccounts = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      const response = await axios.post('/api/custodian/schwab/sync/accounts');
      setSyncResult(response.data);
      await fetchAccounts();
    } catch (err: any) {
      setError('Failed to sync accounts: ' + (err.response?.data?.message || err.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncPositions = async (accountNumber: string) => {
    try {
      setSyncing(true);
      setSyncResult(null);
      const response = await axios.post(`/api/custodian/schwab/sync/positions/${accountNumber}`);
      setSyncResult(response.data);
    } catch (err: any) {
      setError('Failed to sync positions: ' + (err.response?.data?.message || err.message));
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <Container>
        <Card initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <CardContent>
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      {/* Header Card */}
      <Card
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <CardHeader>
          <CardTitle>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3>Schwab Advisor Services</h3>
              <span className="text-xs text-slate-500">Custodian Integration</span>
            </div>
          </CardTitle>
          <StatusBadge status={status?.connected ? 'connected' : 'disconnected'}>
            {status?.connected ? (
              <>
                <CheckCircle2 className="w-3.5 h-3.5" />
                Connected
              </>
            ) : (
              <>
                <XCircle className="w-3.5 h-3.5" />
                Not Connected
              </>
            )}
          </StatusBadge>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert type="error">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <div className="content">{error}</div>
            </Alert>
          )}

          {syncResult && (
            <Alert type={syncResult.success ? 'success' : 'warning'}>
              {syncResult.success ? (
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
              ) : (
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              )}
              <div className="content">
                <strong>Sync Complete:</strong> {syncResult.created} created, {syncResult.updated} updated
                {syncResult.errors.length > 0 && (
                  <ul className="mt-1 ml-4 list-disc">
                    {syncResult.errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                )}
              </div>
            </Alert>
          )}

          {!status?.connected ? (
            <>
              <Alert type="info">
                <Settings className="w-5 h-5 flex-shrink-0" />
                <div className="content">
                  Connect your Schwab Advisor Services account to sync client portfolios, positions, and execute trades.
                  You'll be redirected to Schwab to authorize access.
                </div>
              </Alert>

              <div className="flex gap-3 mt-4">
                <Button
                  variant="primary"
                  onClick={handleConnect}
                  disabled={connecting}
                >
                  {connecting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Link2 className="w-4 h-4" />
                  )}
                  Connect to Schwab
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => window.open('https://developer.schwab.com', '_blank')}
                >
                  <ExternalLink className="w-4 h-4" />
                  Schwab Developer Portal
                </Button>
              </div>
            </>
          ) : (
            <>
              <InfoGrid>
                <InfoItem>
                  <div className="label">Environment</div>
                  <div className="value">
                    {status.environment === 'PRODUCTION' ? 'Production' : 'Sandbox'}
                  </div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Last Sync</div>
                  <div className="value">
                    <Clock className="w-3.5 h-3.5 text-slate-500" />
                    {formatDate(status.lastSyncAt)}
                  </div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Sync Status</div>
                  <div className="value">
                    {status.lastSyncStatus === 'SUCCESS' ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                    ) : status.lastSyncStatus === 'FAILED' ? (
                      <XCircle className="w-3.5 h-3.5 text-red-500" />
                    ) : (
                      <Clock className="w-3.5 h-3.5 text-yellow-500" />
                    )}
                    {status.lastSyncStatus || 'N/A'}
                  </div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Linked Accounts</div>
                  <div className="value">{status.accountCount || accounts.length}</div>
                </InfoItem>
              </InfoGrid>

              <div className="flex gap-3">
                <Button
                  variant="primary"
                  onClick={handleSyncAccounts}
                  disabled={syncing}
                >
                  {syncing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCcw className="w-4 h-4" />
                  )}
                  Sync Accounts
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDisconnect}
                >
                  <Unlink className="w-4 h-4" />
                  Disconnect
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Accounts Card */}
      {status?.connected && (
        <Card
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <CardHeader>
            <CardTitle>
              <Wallet className="w-5 h-5 text-blue-400" />
              <h3>Linked Accounts</h3>
            </CardTitle>
            <span className="text-sm text-slate-500">{accounts.length} accounts</span>
          </CardHeader>

          <CardContent>
            {accounts.length === 0 ? (
              <Alert type="info">
                <Settings className="w-5 h-5 flex-shrink-0" />
                <div className="content">
                  No accounts synced yet. Click "Sync Accounts" to fetch your Schwab accounts.
                </div>
              </Alert>
            ) : (
              <AccountList>
                {accounts.map((account) => (
                  <AccountItem key={account.accountNumber}>
                    <AccountInfo>
                      <div className="icon-wrapper">
                        <Wallet className="w-5 h-5" />
                      </div>
                      <div className="details">
                        <div className="name">
                          {account.type} - ****{account.accountNumber.slice(-4)}
                        </div>
                        <div className="meta">
                          {account.linked ? (
                            <>Linked to {account.portfolioName}</>
                          ) : (
                            <>Not linked to portfolio</>
                          )}
                          {account.lastPositionSync && (
                            <> | Last sync: {formatDate(account.lastPositionSync)}</>
                          )}
                        </div>
                      </div>
                    </AccountInfo>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => handleSyncPositions(account.accountNumber)}
                        disabled={syncing || !account.linked}
                        title={!account.linked ? 'Link to portfolio first' : 'Sync positions'}
                      >
                        <ArrowRightLeft className="w-4 h-4" />
                        Sync
                      </Button>
                      <button
                        onClick={() => setExpandedAccount(
                          expandedAccount === account.accountNumber ? null : account.accountNumber
                        )}
                        className="p-2 text-slate-400 hover:text-white transition-colors"
                      >
                        {expandedAccount === account.accountNumber ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </AccountItem>
                ))}
              </AccountList>
            )}
          </CardContent>
        </Card>
      )}

      {/* Configuration Card */}
      <Card
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <CardHeader>
          <CardTitle>
            <Settings className="w-5 h-5 text-slate-400" />
            <h3>Configuration</h3>
          </CardTitle>
        </CardHeader>

        <CardContent>
          <Alert type="info">
            <Settings className="w-5 h-5 flex-shrink-0" />
            <div className="content">
              <strong>Setup Instructions:</strong>
              <ol className="mt-2 ml-4 list-decimal space-y-1">
                <li>Register your application at the <a href="https://developer.schwab.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Schwab Developer Portal</a></li>
                <li>Set your OAuth redirect URI to: <code className="px-1 py-0.5 bg-slate-800 rounded text-xs">{window.location.origin}/settings/integrations</code></li>
                <li>Add your Client ID and Secret to environment variables:
                  <pre className="mt-1 p-2 bg-slate-800 rounded text-xs overflow-x-auto">
                    SCHWAB_CLIENT_ID=your-client-id{'\n'}
                    SCHWAB_CLIENT_SECRET=your-client-secret
                  </pre>
                </li>
                <li>For sandbox testing, no real accounts are used - Schwab provides test data</li>
              </ol>
            </div>
          </Alert>
        </CardContent>
      </Card>
    </Container>
  );
};

export default SchwabIntegration;
